import React, { useState, useEffect, useCallback } from "react";
import { Container, Modal, Button, Alert } from "react-bootstrap";
import axios from "axios";
import CustomButton from "../../components/button";
import SearchBar from "../../components/searchbar";
import Footer from "../../components/footer";
import SpinnerComp from "../../components/spinner";
import DetailsTable from "../../components/table";
import { toast, ToastContainer } from "react-toastify"; // For toast notifications
import "react-toastify/dist/ReactToastify.css";
import debounce from "lodash.debounce";
import { MdOutlineGppGood } from "react-icons/md";
import { MdOutlineGppBad } from "react-icons/md";

const PendingUsers = ({ user }) => {
  const [users, setUsers] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [loading, setLoading] = useState(true);
  const [totalPages, setTotalPages] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [modalVisible, setModalVisible] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [actionType, setActionType] = useState("");
  const [totalItem, setTotalItem] = useState();

  const pageSize = 8;

  const fetchUsers = useCallback(
    debounce(async (searchQuery, currentPage) => {
      try {
        setLoading(true);
        const response = await axios.get(
          `${process.env.REACT_APP_DEV_BACKEND_URL}/users/all-requests`,
          {
            headers: { Authorization: `Bearer ${user.token}` },
            params: { page: currentPage, size: pageSize, search: searchQuery },
          }
        );
        if (response.status === 200) {
          setUsers(response.data.sellers);
          setTotalPages(response.data.totalPages);
          setTotalItem(response.data.total);
        }
      } catch (error) {
        console.error("Error fetching users:", error);
        toast.error("Failed to fetch users.");
      } finally {
        setLoading(false);
      }
    }, 1000),
    []
  );

  const handleSearch = (query) => {
    setSearchQuery(query);
    setCurrentPage(1); // Reset to the first page on search
  };

  const handleAction = async () => {
    setModalVisible(false);

    if (!selectedUser || !actionType) return;
      // Optimistically update the UI by marking the user as processed
      setUsers((prevUsers) =>
        prevUsers.map((user) =>
          user._id === selectedUser._id ? { ...user, status: actionType } : user
        )
      );
    try {
      setActionLoading(true);
      const response = await axios.post(
        `${process.env.REACT_APP_DEV_BACKEND_URL}/users/${selectedUser._id}/${actionType}`,
        null,
        { headers: { Authorization: `Bearer ${user.token}` } }
      );
      toast.success(response.data.message);


    } catch (error) {
      console.error("Error updating user status:", error);
      toast.error("Failed to update user status. Please try again later.");
    } finally {
      setActionLoading(false);
    }
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const showModal = (user, action) => {


    setSelectedUser(user);
    setActionType(action);
    setModalVisible(true);
  };

  useEffect(() => {
    fetchUsers(searchQuery, currentPage);
  }, [searchQuery, currentPage, fetchUsers]);

  const columns = [
    {
      header: "Name",
      render: (user) => user.name,
    },
    {
      header: "Email",
      render: (user) => user.email,
    },
    {
      header: "Phone",
      render: (user) => `${user.mobile}`,
    },
    {
      header: "Role",
      render: (user) => user.role == "vendor" ? "merchant": user.role,
    },
    {
      header: "Request Date",
      render: (user) =>
        new Date(user.createdAt).toLocaleString("en-GB", { timeZone: "Asia/Dubai" }),
    },
    {
      header: "Action",
      width: "17rem",
      render: (user) => (
        <div className="d-flex justify-content-center gap-2">
          <Button
            style={{
              fontFamily: "Arial, sans-serif",
              borderRadius: "0px",
              border: "0px",
              display: "flex",
              flexDirection: "row",
              alignItems: "center",
              backgroundColor: "transparent",
              color: "green",
            }}
            onClick={() => showModal(user, "accept")}
            disabled={user.status !== "pending"} // Prevent concurrent actions
          >
            <MdOutlineGppGood className="me-1" />Accept
          </Button>
          <div>|</div>
          <Button
            style={{
              fontFamily: "Arial, sans-serif",
              borderRadius: "0px",
              border: "0px",
              display: "flex",
              flexDirection: "row",
              alignItems: "center",
              backgroundColor: "transparent",
              color: "#961f1f",
            }}
            onClick={() => showModal(user, "deny")}
            disabled={user.status !== "pending"} // Prevent concurrent actions
          >
            <MdOutlineGppBad className="me-1" /> Deny
          </Button>
        </div>
      ),
    },
  ];

  return (
    <>
      {loading ? (
        <SpinnerComp />
      ) : (
        <Container
          fluid
          className="px-4 bg-light p-4"
          style={{ minHeight: "92vh" }}
        >
          <h2 style={{ color: "#005A9C" }}>Users List</h2>
          <ToastContainer />

          <SearchBar
            placeholder="Search by Name or Email..."
            value={searchQuery}
            onChange={(e) => handleSearch(e.target.value)}
          />

          <div
            className="border my-3"
            style={{ height: "34rem", overflowY: "auto" }}
          >
            {users.length === 0 ? (
              <div
                className="d-flex justify-content-center align-items-center"
                style={{ height: "100%" }}
              >
                <Alert
                  className="text-center"
                  style={{ width: "50%", backgroundColor: "#005A9C", color: "white" }}
                >
                  No request found.
                </Alert>
              </div>
            ) : (
              <DetailsTable isTransparent={true} data={users} columns={columns} />
            )}
          </div>

          <Footer
            className={"d-flex justify-content-between align-items-center pt-3"}
            text={`${totalItem} Total Count`}
            pageSize={pageSize}
            totalPages={totalPages}
            currentPage={currentPage}
            setCurrentPage={handlePageChange}
          />
        </Container>
      )}

      {/* Confirmation Modal */}
      <Modal show={modalVisible} onHide={() => setModalVisible(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>Confirm Action</Modal.Title>
        </Modal.Header>
        <Modal.Body>
            <p>
              Are you sure you want to <strong>{actionType}</strong> this user?
            </p>
        </Modal.Body>
        <Modal.Footer>
          <CustomButton
            variant="secondary"
            onClick={() => setModalVisible(false)}
            disabled={actionLoading}
          >
            Cancel
          </CustomButton>
          <CustomButton
            variant={actionType === "accept" ? "success" : "danger"}
            onClick={handleAction}
            disabled={actionLoading}
          >
            {"Confirm"}
          </CustomButton>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default PendingUsers;
